<script setup>
// Conversation sidebar: finds the customer by the contact phone (or registers it), lists the
// upcoming appointments of this customer and opens the booking dialog pre-linked to the conversation.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AppointmentDialog from './AppointmentDialog.vue';
import {
  appointmentsAPI,
  customerByPhone,
  customersAPI,
  partnersAPI,
  practitionersAPI,
  servicesAPI,
} from '../api';
import { STATUS_STYLE, fmtDateTime, localeTag, toDateInput } from '../helpers';

const props = defineProps({
  contact: { type: Object, default: () => ({}) },
  conversationId: { type: [Number, String], default: null },
});
const { t, locale } = useI18n();
const router = useRouter();
const lt = computed(() => localeTag(locale.value));

const isLoading = ref(true);
const customer = ref(null);
const upcoming = ref([]);
const practitioners = ref([]);
const services = ref([]);
const partners = ref([]);
const dialogRef = ref(null);

const phone = computed(() => props.contact?.phone_number || '');

const loadUpcoming = async () => {
  if (!customer.value) {
    upcoming.value = [];
    return;
  }
  const { data } = await appointmentsAPI.list({
    customer_id: customer.value.id,
    from: new Date().toISOString(),
    to: new Date(Date.now() + 180 * 86400000).toISOString(),
  });
  upcoming.value = (data.payload || [])
    .filter(a => a.status !== 'cancelled')
    .slice(0, 5);
};
const load = async () => {
  isLoading.value = true;
  try {
    customer.value = phone.value
      ? (await customerByPhone(phone.value)).data.payload
      : null;
    await loadUpcoming();
  } finally {
    isLoading.value = false;
  }
};
const ensureCatalog = async () => {
  if (practitioners.value.length) return;
  const [pr, sv, pa] = await Promise.all([
    practitionersAPI.list({ active: 'true' }),
    servicesAPI.list({ active: 'true' }),
    partnersAPI.list(),
  ]);
  practitioners.value = pr.data.payload || [];
  services.value = sv.data.payload || [];
  partners.value = pa.data.payload || [];
};
const register = async () => {
  try {
    const { data } = await customersAPI.create({
      name: props.contact.name || phone.value,
      phone: phone.value,
      email: props.contact.email,
      contact_id: props.contact.id,
    });
    customer.value = data;
  } catch (error) {
    useAlert(t('WASAPI_APPOINTMENTS.COMMON.SAVE_ERROR'));
  }
};
const book = async () => {
  await ensureCatalog();
  dialogRef.value?.open({
    prefill: {
      customer: customer.value,
      date: toDateInput(new Date(Date.now() + 86400000)),
      conversation_id: props.conversationId,
    },
  });
};
const openAgenda = () =>
  router.push({
    name: 'wasapi_appointments',
    params: { accountId: router.currentRoute.value.params.accountId },
  });

watch(() => props.contact?.id, load);
onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-2 py-2 text-sm">
    <div v-if="isLoading" class="flex justify-center py-2"><Spinner /></div>
    <template v-else>
      <p v-if="!phone" class="text-xs text-n-slate-11">
        {{ t('WASAPI_APPOINTMENTS.PANEL.NO_PHONE') }}
      </p>
      <template v-else-if="!customer">
        <p class="text-xs text-n-slate-11">
          {{ t('WASAPI_APPOINTMENTS.PANEL.NOT_REGISTERED') }}
        </p>
        <Button
          :label="t('WASAPI_APPOINTMENTS.PANEL.REGISTER')"
          icon="i-lucide-user-plus"
          variant="faded"
          color="slate"
          size="sm"
          @click="register"
        />
      </template>
      <template v-else>
        <span class="text-xs font-medium uppercase text-n-slate-10">
          {{ t('WASAPI_APPOINTMENTS.PANEL.UPCOMING') }}
        </span>
        <p v-if="!upcoming.length" class="text-xs text-n-slate-11">
          {{ t('WASAPI_APPOINTMENTS.PANEL.NONE_UPCOMING') }}
        </p>
        <button
          v-for="a in upcoming"
          :key="a.id"
          type="button"
          class="flex flex-col gap-0.5 px-2 py-1.5 text-left rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2"
          @click="openAgenda"
        >
          <span class="text-xs font-medium text-n-slate-12">{{
            fmtDateTime(a.appointment_at, lt)
          }}</span>
          <span class="text-[0.6875rem] text-n-slate-11 truncate">
            {{ a.service_name }} · {{ a.practitioner_name }}
          </span>
          <span
            class="self-start px-1.5 py-0.5 text-[0.625rem] rounded"
            :class="STATUS_STYLE[a.status]?.badge"
          >
            {{ t(`WASAPI_APPOINTMENTS.STATUS.${a.status}`) }}
          </span>
        </button>
        <Button
          :label="t('WASAPI_APPOINTMENTS.PANEL.BOOK')"
          icon="i-lucide-calendar-plus"
          color="blue"
          size="sm"
          @click="book"
        />
      </template>
    </template>
    <AppointmentDialog
      ref="dialogRef"
      :practitioners="practitioners"
      :services="services"
      :partners="partners"
      @saved="loadUpcoming"
    />
  </div>
</template>
